// Uniform clamped cubic B-splines on t ∈ [0, 1], given by their number of control points nc (>= 4):
// knots 0,0,0,0, 1/(nc-3), …, 1,1,1,1. The curve passes through its first and last control points.
// Smoothing fits penalise second divided differences of the control points over their Greville
// abscissae, which vanish for straight lines (and planes), so smoothing never bends a line.
import { Band, solveSPD } from './linalg.ts';

const knotCache = new Map<number, Float64Array>();
export function knots(nc: number) {
  let U = knotCache.get(nc);
  if (!U) {
    U = new Float64Array(nc + 4);
    for (let k = 0; k < nc + 4; k++) U[k] = Math.min(1, Math.max(0, (k - 3) / (nc - 3)));
    knotCache.set(nc, U);
  }
  return U;
}

/** Greville abscissae: where each control point "sits" on [0, 1]. */
export function greville(nc: number) {
  const U = knots(nc), g = new Float64Array(nc);
  for (let k = 0; k < nc; k++) g[k] = (U[k + 1] + U[k + 2] + U[k + 3]) / 3;
  return g;
}

const left = new Float64Array(4), right = new Float64Array(4), n2 = new Float64Array(3);

/**
 * The 4 non-zero basis values at t into N (and their derivatives d/dt into D), for control points
 * s..s+3; returns s.
 */
export function basis(t: number, nc: number, N: Float64Array, D?: Float64Array): number {
  const U = knots(nc), sp = nc - 3;
  t = t < 0 ? 0 : t > 1 ? 1 : t;
  const s = Math.min(Math.floor(t * sp), sp - 1), i = s + 3;
  N[0] = 1;
  for (let j = 1; j <= 3; j++) {   // Piegl & Tiller A2.2
    left[j] = t - U[i + 1 - j];
    right[j] = U[i + j] - t;
    let saved = 0;
    for (let r = 0; r < j; r++) {
      const tmp = N[r] / (right[r + 1] + left[j - r]);
      N[r] = saved + right[r + 1] * tmp;
      saved = left[j - r] * tmp;
    }
    N[j] = saved;
    if (j === 2 && D) { n2[0] = N[0]; n2[1] = N[1]; n2[2] = N[2]; }
  }
  if (D) {
    // N'_{k,3} = 3 N_{k,2} / (U[k+3] - U[k]) - 3 N_{k+1,2} / (U[k+4] - U[k+1]); the degree-2 values are N_{s+1..s+3,2}
    for (let a = 0; a < 4; a++) {
      const k = s + a, lo = a >= 1 ? n2[a - 1] : 0, hi = a <= 2 ? n2[a] : 0;
      const d1 = U[k + 3] - U[k], d2 = U[k + 4] - U[k + 1];
      D[a] = (d1 > 0 ? (3 * lo) / d1 : 0) - (d2 > 0 ? (3 * hi) / d2 : 0);
    }
  }
  return s;
}

const Nb = new Float64Array(4), Db = new Float64Array(4);

/** Point of a curve (control points packed xyz, nc of them) at t into out; its derivative into d. */
export function curve(c: ArrayLike<number>, nc: number, t: number, out: number[] | Float64Array, d?: number[] | Float64Array) {
  const s = basis(t, nc, Nb, d ? Db : undefined);
  out[0] = out[1] = out[2] = 0;
  if (d) d[0] = d[1] = d[2] = 0;
  for (let a = 0; a < 4; a++) for (let k = 0; k < 3; k++) {
    out[k] += Nb[a] * c[3 * (s + a) + k];
    if (d) d[k] += Db[a] * c[3 * (s + a) + k];
  }
  return out;
}

/** A scalar spline (control values y) at t. */
export function value(y: ArrayLike<number>, nc: number, t: number) {
  if (nc === 1) return y[0];
  const s = basis(t, nc, Nb);
  return Nb[0] * y[s] + Nb[1] * y[s + 1] + Nb[2] * y[s + 2] + Nb[3] * y[s + 3];
}

const Nu = new Float64Array(4), Du = new Float64Array(4), Nv = new Float64Array(4), Dv = new Float64Array(4);

/** Tensor surface (h[i * nv + j]) at (a, b) ∈ [0, 1]²: [value, ∂/∂a, ∂/∂b]. */
export function surf(h: ArrayLike<number>, nu: number, nv: number, a: number, b: number, out: number[] | Float64Array = [0, 0, 0]) {
  const su = basis(a, nu, Nu, Du), sv = basis(b, nv, Nv, Dv);
  let f = 0, fa = 0, fb = 0;
  for (let i = 0; i < 4; i++) for (let j = 0; j < 4; j++) {
    const x = h[(su + i) * nv + sv + j];
    f += Nu[i] * Nv[j] * x; fa += Du[i] * Nv[j] * x; fb += Nu[i] * Dv[j] * x;
  }
  out[0] = f; out[1] = fa; out[2] = fb;
  return out;
}

/** Rows of the second divided differences over the Greville abscissae (scaled by len, the physical length of [0, 1]). */
export function diff2(nc: number, len = 1): { k: number; w: [number, number, number] }[] {
  const g = greville(nc), rows: { k: number; w: [number, number, number] }[] = [];
  for (let k = 0; k + 2 < nc; k++) {
    const h1 = (g[k + 1] - g[k]) * len, h2 = (g[k + 2] - g[k + 1]) * len, m = (h1 + h2) / 2;
    rows.push({ k, w: [1 / (h1 * m), -(1 / h1 + 1 / h2) / m, 1 / (h2 * m)] });
  }
  return rows;
}

/** DᵀD of diff2 into a band, for the unknowns stride * k + off. */
export function penalty1(nc: number, stride = 1, off = 0, into?: Band, len = 1) {
  const P = into ?? new Band(stride * nc, 2 * stride);
  for (const { k, w } of diff2(nc, len)) for (let a = 0; a < 3; a++) for (let b = 0; b <= a; b++)
    P.add(stride * (k + a) + off, stride * (k + b) + off, w[a] * w[b]);
  return P;
}

/**
 * Thin-plate penalty ∫ f_aa² + 2 f_ab² + f_bb² of a tensor surface (h[i * nv + j]) on a domain la x lb
 * (metres), from divided differences of the control values.
 */
export function penalty2(nu: number, nv: number, la: number, lb: number) {
  const P = new Band(nu * nv, 3 * nv + 3), du = diff2(nu, la), dv = diff2(nv, lb);
  const add = (ix: number[], w: number[], s: number) => {
    for (let a = 0; a < ix.length; a++) for (let b = 0; b <= a; b++) P.add(ix[a], ix[b], s * w[a] * w[b]);
  };
  for (let j = 0; j < nv; j++) for (const { k, w } of du) add([k * nv + j, (k + 1) * nv + j, (k + 2) * nv + j], w, 1);
  for (let i = 0; i < nu; i++) for (const { k, w } of dv) add([i * nv + k, i * nv + k + 1, i * nv + k + 2], w, 1);
  const ga = greville(nu), gb = greville(nv);
  for (let i = 0; i + 1 < nu; i++) for (let j = 0; j + 1 < nv; j++) {
    const ha = (ga[i + 1] - ga[i]) * la, hb = (gb[j + 1] - gb[j]) * lb, s = 1 / (ha * hb);
    add([i * nv + j, i * nv + j + 1, (i + 1) * nv + j, (i + 1) * nv + j + 1], [s, -s, -s, s], 2);
  }
  return P;
}

/**
 * Weighted smoothing fit of a scalar spline with nc control values to (ts, ys, w); s is the
 * dimensionless smoothness (0: plain least squares; the penalty is scaled to the data term).
 */
export function smoothFit1(ts: ArrayLike<number>, ys: ArrayLike<number>, w: ArrayLike<number> | null, nc: number, s: number): Float64Array {
  if (nc === 1) {
    let a = 0, b = 0;
    for (let i = 0; i < ts.length; i++) { const wi = w ? w[i] : 1; a += wi * ys[i]; b += wi; }
    return Float64Array.of(b ? a / b : 0);
  }
  const A = new Band(nc, 3), rhs = new Float64Array(nc);
  for (let i = 0; i < ts.length; i++) {
    const wi = w ? w[i] : 1;
    if (!wi) continue;
    const k = basis(ts[i], nc, Nb);
    for (let a = 0; a < 4; a++) {
      rhs[k + a] += wi * Nb[a] * ys[i];
      for (let b = 0; b <= a; b++) A.add(k + a, k + b, wi * Nb[a] * Nb[b]);
    }
  }
  if (s > 0) { const P = penalty1(nc); A.addScaled(P, lambda(s, A, P)); }
  return solveSPD(A, rhs);
}

/** The penalty weight for smoothness s: s times the data term's scale over the penalty's. */
export const lambda = (s: number, A: Band, P: Band) => { const tp = P.trace(); return tp > 0 ? (s * A.trace()) / tp : 0; };
